import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Comment } from "../../types/Types";
import { formatDMYHM, formatTimeAgo } from "../../utils/DateUtils";
import Button from "./Button";
import { FaRegComment } from "react-icons/fa";
import { ButtonTypes, DEFAULT_AUTH_DATA } from "../../utils/Constants";
import { CiTrash } from "react-icons/ci";

interface CommentProps {
  comments: Comment[];
  onSubmit: (text: string) => void;
  onDelete: (id: number) => void;
  isSubmitting?: boolean;
  ownerId?: string | number; // the project owner's comments get an "Owner" tag
}

const CommentSection: React.FC<CommentProps> = ({
  comments,
  onSubmit,
  onDelete,
  isSubmitting = false,
  ownerId,
}) => {
  const [newComment, setNewComment] = useState("");
  const authData = localStorage.getItem("auth_data") ? JSON.parse(localStorage.getItem("auth_data")!) : DEFAULT_AUTH_DATA;

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    await onSubmit(text);
    setNewComment("");
  };

  const myInitial = (authData?.username || "?").charAt(0).toUpperCase();

  return (
    <section className="768:p-6 p-4 bg-white border border-gray-e8 rounded-3xl shadow-md flex flex-col gap-4">
      <div className="flex items-baseline gap-2.5">
        <h2 className="text-2xl font-semibold flex items-center gap-2">Comments <FaRegComment className="text-xl" /></h2>
        <span className="text-sm text-gray-500">{comments.length}</span>
      </div>

      {authData?.id ? (
        <div className="flex gap-2.5 items-start">
          <span className="w-8 h-8 rounded-full bg-secondary text-primary grid place-items-center font-bold text-sm shrink-0 mt-1" aria-hidden="true">
            {myInitial}
          </span>
          <div className="flex-1 min-w-0">
            <textarea
              className="w-full border border-[#D9D9D9] rounded-2xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              rows={2}
              placeholder="Ask a question or leave feedback…"
              aria-label="Write a comment"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <div className="mt-2 flex justify-end">
              <Button
                type={ButtonTypes.PRIMARY}
                onClick={handleAddComment}
                disabled={isSubmitting || !newComment.trim()}
                className="!px-4 !py-2 font-semibold disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Posting..." : "Post comment"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          <Link to="/login" className="font-semibold text-primary underline underline-offset-[3px]">Log in</Link> to add a comment.
        </p>
      )}

      {comments.length > 0 && (
        <ul className="flex flex-col gap-3">
          {comments.map((c) => {
            const isMine = authData?.id != null && String(authData.id) === String(c.user);
            const isOwner = ownerId != null && String(ownerId) === String(c.user);
            return (
              <li key={c.id} className="flex gap-3">
                <Link to={`/profile/${c.user}`} className="shrink-0 mt-1">
                  <img
                    src={c.user_info.profile_photo || "/Images/UserProfile.png"}
                    alt=""
                    className="w-8 h-8 border rounded-full object-cover"
                  />
                </Link>
                <div className="flex-1 min-w-0 bg-gray-f6 rounded-[4px_16px_16px_16px] px-3.5 py-2.5">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <Link to={`/profile/${c.user}`} className="text-sm font-semibold hover:underline">
                      {c.user_info.username}
                    </Link>
                    {isOwner && (
                      <span className="text-[11px] font-bold text-primary bg-primary/[0.07] rounded-full px-2 py-px">Owner</span>
                    )}
                    <span className="text-xs text-gray-500" title={formatDMYHM(c.created_at)}>
                      {formatTimeAgo(c.created_at)}
                    </span>
                    {isMine && (
                      <button
                        type="button"
                        className="ml-auto flex items-center gap-1 text-xs font-semibold text-red-primary hover:underline"
                        onClick={() => onDelete(c.id)}
                      >
                        <CiTrash className="text-base" /> Delete
                      </button>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words">{c.text}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default CommentSection;
